import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '@iq/react-components';
import { v4 as uuidv4 } from 'uuid';
import { capitalize, extractCategories } from '../../../../../utils';

import SectionItem from '../SectionItem';
import Delete from '../../../../Delete';
import JsonEditor from '../../../../JsonEditor';
import Heading from '../../../../Heading';

import {
  getExtendedPanelSchema,
  getFilteredPanelTypes,
  uniquePerPagePanelTypes,
} from '../../../../panels/panelSchema';
import { getComponentsPanelSchema } from '../../../../panels/ComponentsPanel';

import { getFilesPanelSchema } from '../../../../panels/FilesPanel';
import { textPanelSchema } from '../../../../panels/TextPanel';
import { tfrPanelSchema } from '../../../../panels/TfrPanel';
import { getVisualizationsPanelSchema } from '../../../../panels/VisualizationsPanel';
import { healthPanelSchema } from '../../../../panels/HealthPanel';
import { getViewerPanelSchema, viewerPanelUiSchema } from '../../../../panels/ViewerPanel';
import { getPageSchema, pageUiSchema } from '../../schemas/pageSchema';
import { getComponentAttributesPanelSchema } from '../../../../panels/ComponentAttributesPanel';
import config from '../../../../../config';
import {
  getAnalyticsTablePanelSchema,
  getSwitchgearCounterSources,
  shouldShowAnalyticsTablePanelType,
} from '../../../../panels/AnalyticsTablePanel';

import { getStaticComponents, getComponentsLoaded } from '../../../../../bundles/components';
import {
  requestSources,
  getSources,
  getSiteVariables,
  getSourcesLoaded,
} from '../../../../../bundles/sources';
import { requestTypes, getTypes } from '../../../../../bundles/types';
import { requestTags, getTags } from '../../../../../bundles/tags';
import { getVisualizations } from '../../../../../bundles/visualizations';
import { getActiveSite } from '../../../../../bundles/sites';
import { requestUserEventTypes, getUserEventTypes } from '../../../../../bundles/events';
import { notifyPageChanges } from '../../../../../bundles/layouts';
import { getPagePanelTypeCounts } from '../../../../../bundles/layouts/selectors';
import { getEventTimeLinePanelSchema } from '../../../../panels/EventTimelinePanel/jsonSchema';

// To remove duplicate error message
const transformErrors = (errors) => errors.map((error) => ({ ...error, message: '' }));

const EditPage = ({
  page,
  activeTree = {},
  setActiveTreeIndexes,
  updatePages,
  updatePage,
  updateSection,
  updateColumn,
}) => {
  const { title, path, icon, activeComponentFilter, pageType, viewer, sections = [] } = page;
  const dispatch = useDispatch();
  const { id: siteId } = useSelector(getActiveSite);

  const types = useSelector(getTypes);
  const tags = useSelector(getTags);
  const visualizations = useSelector(getVisualizations);
  const staticComponents = useSelector(getStaticComponents);
  const componentsLoaded = useSelector(getComponentsLoaded);
  const sources = useSelector(getSources);
  const sourcesLoaded = useSelector(getSourcesLoaded);
  const variables = useSelector(getSiteVariables);
  const pagePanelTypeCounts = useSelector((state) =>
    getPagePanelTypeCounts(state, siteId, activeTree.page)
  );

  const eventTypes = useSelector(getUserEventTypes);
  const [viewerData, setViewerData] = React.useState(viewer || {});

  useEffect(() => {
    dispatch(requestTypes(siteId));
    dispatch(requestTags(siteId));
    if (sourcesLoaded !== siteId) dispatch(requestSources({ siteId }));
    dispatch(requestUserEventTypes());
  }, [dispatch, siteId, sourcesLoaded]);

  const {
    page: activePageIndex,
    section: activeSectionIndex,
    column: activeColumnIndex,
    panel: activePanelIndex,
  } = activeTree;

  useEffect(() => {
    setViewerData(viewer || {});
  }, [activePageIndex, viewer]);

  const sortedComponents = useMemo(
    () => staticComponents.toSorted((a, b) => a.itemDesignation.localeCompare(b.itemDesignation)),
    [staticComponents]
  );

  const componentsWithModels = useMemo(
    () => sortedComponents.filter((component) => component.models?.length > 0),
    [sortedComponents]
  );

  useEffect(() => {
    if (componentsLoaded === siteId && viewerData?.modelSourceComponent) {
      const hasConfiguredComponentWithModels = componentsWithModels.some(
        (component) => component.id === viewerData.modelSourceComponent
      );

      if (!hasConfiguredComponentWithModels) {
        setViewerData((previousViewerSettings) => ({
          ...previousViewerSettings,
          modelSourceComponent: null,
          modelSourceComponentDefaultModel: null,
        }));
      }
    }
  }, [componentsLoaded, siteId, componentsWithModels, viewerData?.modelSourceComponent]);

  const sourceVariables = useMemo(() => {
    const variablesWithSourceNames = [];

    for (const variable of variables) {
      const source = sources.find((s) => s.id === variable.source_id);
      variablesWithSourceNames.push({ ...variable, sourceName: source?.name });
    }

    return variablesWithSourceNames;
  }, [sources, variables]);

  const updatedPageSchema = useMemo(() => getPageSchema(sortedComponents), [sortedComponents]);
  const switchgearSources = useMemo(() => getSwitchgearCounterSources(sources), [sources]);
  const showAnalyticsTable = useMemo(
    () => shouldShowAnalyticsTablePanelType(sources, config.onPremise),
    [sources]
  );

  const updatedFilesPanelSchema = useMemo(
    () => getFilesPanelSchema(types || [], tags || []),
    [types, tags]
  );

  const activePanelOptions = useMemo(
    () =>
      sections?.[activeSectionIndex]?.columns?.[activeColumnIndex]?.panels?.[activePanelIndex]
        ?.options || {},
    [sections, activeSectionIndex, activeColumnIndex, activePanelIndex]
  );

  const updatedEventTimelineSchema = useMemo(
    () => getEventTimeLinePanelSchema(eventTypes, activePanelOptions),
    [eventTypes, activePanelOptions]
  );

  const updatedVisualizationsPanelSchema = useMemo(
    () => getVisualizationsPanelSchema(visualizations, tags || []),
    [visualizations, tags]
  );

  const updatedComponentsPanelSchema = useMemo(
    () => getComponentsPanelSchema(sortedComponents),
    [sortedComponents]
  );

  const updatedComponentAttributesPanelSchema = useMemo(() => {
    const categories = extractCategories(sortedComponents);
    return getComponentAttributesPanelSchema(categories);
  }, [sortedComponents]);

  const updatedAnalyticsTablePanelSchema = useMemo(
    () => getAnalyticsTablePanelSchema(switchgearSources, config.onPremise),
    [switchgearSources]
  );

  const extendedPanels = useMemo(() => {
    const basePanels = {
      components: updatedComponentsPanelSchema,
      'event-timeline': updatedEventTimelineSchema,
      files: updatedFilesPanelSchema,
      text: textPanelSchema,
      'tfr-viewer': tfrPanelSchema,
      visualizations: updatedVisualizationsPanelSchema,
      health: healthPanelSchema,
      'component-properties': updatedComponentAttributesPanelSchema,
    };

    if (showAnalyticsTable) {
      basePanels['analytics-table'] = updatedAnalyticsTablePanelSchema;
    }

    return basePanels;
  }, [
    updatedComponentsPanelSchema,
    updatedEventTimelineSchema,
    updatedFilesPanelSchema,
    updatedVisualizationsPanelSchema,
    updatedComponentAttributesPanelSchema,
    updatedAnalyticsTablePanelSchema,
    showAnalyticsTable,
  ]);

  const filteredViewerPanelTypes = useMemo(() => {
    const excludeTypes = [];
    const viewerPanelType = viewerData?.panels?.[0]?.type;

    uniquePerPagePanelTypes.forEach((panelType) => {
      const panelTypeCount = pagePanelTypeCounts.get(panelType) ?? 0;

      if (panelTypeCount > 0 && viewerPanelType !== panelType) {
        excludeTypes.push(panelType);
      }
    });

    return getFilteredPanelTypes(excludeTypes);
  }, [pagePanelTypeCounts, viewerData]);

  const updatedViewerItemPanelSchema = useMemo(
    () => getExtendedPanelSchema(extendedPanels, [], filteredViewerPanelTypes),
    [extendedPanels, filteredViewerPanelTypes]
  );

  const updatedViewerPanelSchema = useMemo(
    () =>
      getViewerPanelSchema(
        updatedViewerItemPanelSchema,
        sourceVariables,
        sortedComponents,
        componentsWithModels
      ),
    [updatedViewerItemPanelSchema, sourceVariables, sortedComponents, componentsWithModels]
  );

  const onFormChange = useCallback(
    ({ formData: viewerProperties }) =>
      setViewerData((previousData) => ({ ...previousData, ...viewerProperties })),
    []
  );

  const updateViewerProperties = useCallback(
    ({ formData: viewerProperties }) =>
      updatePage(activePageIndex, (page) => ({ ...page, viewer: viewerProperties })),
    [updatePage, activePageIndex]
  );

  const updatePageProperties = useCallback(
    ({ formData: pageProperties }) =>
      updatePage(activePageIndex, (page) => ({ ...page, ...pageProperties })),
    [updatePage, activePageIndex]
  );

  const createSection = useCallback(() => {
    setActiveTreeIndexes({ page: activePageIndex, section: sections.length });
    updatePage(activePageIndex, (page) => ({
      ...page,
      sections: [
        ...page.sections,
        {
          id: `section-${uuidv4()}`,
          title: 'Untitled section',
          columns: [],
          displayWhen: { componentIsActive: null },
        },
      ],
    }));
  }, [activePageIndex, sections.length, setActiveTreeIndexes, updatePage]);

  const deletePage = useCallback(() => {
    setActiveTreeIndexes();
    updatePages((pages) => pages.filter((_, index) => index !== activePageIndex));
    dispatch(notifyPageChanges('deletePage'));
  }, [setActiveTreeIndexes, updatePages, activePageIndex, dispatch]);

  const moveSection = (from, to) => {
    updatePage(activePageIndex, (page) => {
      const section = page.sections[from];
      const updatedSections = [...page.sections];
      updatedSections.splice(from, 1);
      updatedSections.splice(to, 0, section);
      return { ...page, sections: updatedSections };
    });
    setActiveTreeIndexes({ ...activeTree, section: to, initial: false });
  };

  const removeSection = (index) => {
    setActiveTreeIndexes({ page: activePageIndex });
    updatePage(activePageIndex, (page) => {
      const updatedSections = [...page.sections];
      updatedSections.splice(index, 1);
      return { ...page, sections: updatedSections };
    });
  };

  // If we upgrade to RJSFv5, we can try to get rid of the custom validation.
  const customValidate = useCallback(
    (formData, errors) => {
      for (const fieldName of updatedPageSchema.required) {
        const validValue = formData[fieldName] || formData[fieldName] === 0;
        if (!validValue) {
          errors[fieldName].addError(`Page ${capitalize(fieldName)} is required`);
        }
      }

      return errors;
    },
    [updatedPageSchema]
  );
  return (
    <div className="edit-page-component">
      <Heading
        typeLabel="page"
        contentLeft={<div className="title">{title}</div>}
        large
      />

      <JsonEditor
        title="Page properties"
        schema={updatedPageSchema}
        uiSchema={pageUiSchema}
        formData={{ title, path, icon, activeComponentFilter, pageType }}
        onFormSubmit={updatePageProperties}
        customValidate={customValidate}
        customTransformErrors={(errors) => transformErrors(errors)}
      />

      {!config.onPremise && (
        <JsonEditor
          title="Top/Viewer section"
          formData={viewerData}
          context={viewerData}
          schema={updatedViewerPanelSchema}
          uiSchema={viewerPanelUiSchema}
          onFormChange={onFormChange}
          onFormSubmit={updateViewerProperties}
          saveButtonText={'Update viewer properties'}
          initialEditMode={activeSectionIndex === 'viewer'}
        />
      )}

      <Heading
        contentLeft={
          <>
            <div className="title">Sections</div>
            <Button
              onClick={createSection}
              activity="secondary"
              slim
            >
              Create section
            </Button>
          </>
        }
      />

      <div className="page-content">
        <div className="section-list">
          {sections.map((section, index) => {
            const key = `section-${index}`;
            return (
              <SectionItem
                key={key}
                activeTree={activeTree}
                sectionIndex={index}
                sectionCount={sections.length}
                moveSection={moveSection}
                removeSection={removeSection}
                updateSection={updateSection}
                updateColumn={updateColumn}
                sectionActive={activeTree.section === index}
                setActiveTreeIndexes={setActiveTreeIndexes}
                {...section}
              />
            );
          })}
        </div>
        <Delete
          onDelete={deletePage}
          title="Delete page"
          pageName={title}
        />
      </div>
    </div>
  );
};

export default EditPage;
